import { Product } from '@setvi/shared/interfaces';
import { CompetitorInterface } from 'Services/Query/Competitors/Types';
import {
  RfqAiOriginal,
  RfqAlternate,
  RfqInternalShareEntry
} from 'Services/Query/Auto-RFQ/Types';

export interface InternalProducts extends Product {
  external: boolean;
}

export const enum RfqMatchOverride {
  UserSelection = 'UserSelection',
  MarkedAsNonMatched = 'MarkedAsNonMatched'
}

export interface RFQProducts {
  // Is used to show current matchedInternalPorudct
  internalIndex?: number;
  prevItemPercentage?: number;
  fileId?: string;
  id?: string;
  productName: string;
  competitorSku: string;
  fileUrl: string;
  desc: string;
  percentage?: number;
  manufacturer: string;
  manufacturerSku: string;
  matchedInternalProducts: InternalProducts[];
  matchedInternalProductsId: object[];
  quantity: number | string;
  position: number;
  specifications?: string;
  unitPrice?: string;
  ignored?: boolean;
  mfgSkuMatched?: boolean;
  foundOnline: boolean;
  foundItem?: {
    name: string;
    url: string;
    manufacturer: string;
    manufacturerSku: string;
    isManufacturerExistInDB: boolean;
  };
  customAttributes: {
    label: string;
    value: string;
  }[];
  summary: {
    description: string;
    specifications?: {
      label: string;
      value: string;
    }[];
  };
  sell?: number;
  cost?: number;
  // Value used as indicator if product is from current socket session
  new?: boolean;
  statusId?: string;
  manualEntry?: boolean;
  isMatched: boolean;
  inQuote?: boolean;
  matchOverride?: RfqMatchOverride;
  attributeLineNo?: number;
  excelRow?: number;
  originalOrder?: number;
  customerNote?: string;
  internalNotesCount?: number;
  lastModifiedBy?: {
    userId: string;
    name: string;
    email?: string;
  };
  lastModifiedDate?: string;
  _isSkeleton?: boolean;
  // Alternate Items (QOT-1580) — present only when the RFQ has the flag on.
  isPreferredVendor?: boolean;
  countPreferredVendors?: number;
  countPrivateLabels?: number;
  alternates?: RfqAlternate[];
  topAiOriginal?: RfqAiOriginal | null;
}

export interface RFQAcl {
  canAccessQuote: boolean;
  canDeleteQuote: boolean;
  canEditQuote: boolean;
  canExportCSV: boolean;
  canExportPDF: boolean;
  canShareQuote: boolean;
}

export interface RFQList {
  __v: number;
  _id: string;
  name: string;
  createdDate: Date;
  description: string;
  totalProducts: number;
  lastModifiedDate: Date;
  status?: string;
  // Alternate Items (QOT-1580) — set at creation, immutable after.
  alternatesEnabled?: boolean;
  competitor: CompetitorInterface | null;
  competitorId?: string;
  user: { _id: number; name: string; email: string };
  internalShare?: RfqInternalShareEntry[];
  files: {
    name: string;
    url: string;
    statusId?: string;
    total?: number;
  }[];
  totalItems: number;
  quoteId?: string;
  // Access control for the linked quote, mirroring the quotes-v2 `acl` shape.
  // `canExportPDF` folds in required-info completeness (previewInfo
  // preparedBy/preparedFor). Absent for older responses / RFQs with no quote.
  acl?: RFQAcl;
  analytics: {
    matched: number;
    mfgMatched: number;
    nonMfgMatched: number;
    notMatched: number;
    onlineMatched: number;
    processed: number;
    selected: number;
    time: string;
    privateLabelMatched?: number;
    privateLabelSwapped?: number;
    fileProcessingTime?: number;
  };
  quoteSummary?: {
    account?: {
      _id: string;
      name: string;
      accountNumber?: string;
    };
    prospect?: {
      _id: string;
      name: string;
    };
    customerName?: string;
    totalSell?: number;
    totalCost?: number;
    totalGp?: number;
    status?: string;
  };
}

export interface CompetitorProps {
  name: string;
  sku: string;
  manufacturer: string;
  manufacturerSku: string;
  price: string;
  quantity?: string | number;
}

export type RfqListFieldKey =
  | 'created_date'
  | 'rfq_name'
  | 'quote_name'
  | 'status'
  | 'customer_name'
  | 'competitor'
  | 'total_items'
  | 'total_price'
  | 'total_gp'
  | 'last_updated';

export interface RfqListField {
  key: RfqListFieldKey;
  label: string;
  field: string;
  minWidth?: string;
  type: 'date' | 'number' | 'text' | 'currency';
}

export interface ConfigurationProps {
  _id: string;

  // RFQ List Fields
  rfqListFields?: RfqListField[];

  // Quote Settings
  validUntil?: number;
  enableCRMIntegration?: boolean;
  enableOrderHistory?: boolean;
  enableApprovalProcess?: boolean;
  enableOpportunity?: boolean;
  enableShipTo?: boolean;
  marginPercentage: number;
  enableSpecialPurchaseRequest: boolean;
  enableEquipmentRequest: boolean;
  enableValidityDate?: boolean;
  enableNewProspect?: boolean;
  fullEditableQuotes?: boolean;
  nonEditableSellTypes?: string[];

  // Pricing Configuration
  pricing?: {
    mode: 'NO_PRICING' | 'COST_ONLY' | 'CUSTOMER_PRICING';
    defaultMarginEnabled: boolean;
    defaultMarginPercent: number;
  };

  // RFQ Settings (consolidated from former rfqConfiguration)
  rfqToQuoteEnabled?: boolean; // Enable RFQ to Quote conversion
  enableAccountSelection?: boolean; // Show customer selection in RFQ
  enableProspect?: boolean; // Enable prospect entry in RFQ
  requireSegmentForProspect?: boolean; // If true, user must select segment
  defaultSegment?: string; // Segment ID auto-applied by backend
  showManufacturerDetails?: boolean; // Show Manufacturer details in RFQ PDF

  // Alternate Items (Private Label + Preferred Vendor) — QOT-1580
  alternateItemsEnabled?: boolean; // Tenant rollout flag: feature available at all
  alternatesDefaultOn?: boolean; // Default state of the create-dialog checkbox
}
